import React, { useState, useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { ThemeContext } from 'styled-components';
import Img from 'gatsby-image';

import device from '../utils/device';
import { isDateInPast } from '../utils/date';
import SEO from '../components/Seo';
import GlobalStyle2 from '../components/GlobalStyle2';
import R from '../components/images/R';
import Logo from '../components/images/Logo';
import LogoTaFlat from '../images/ta_logo_flat.png';
import VideoButton from '../components/images/VideoButton';
import VideoPager from '../components/VideoPager';
import VideoCarousel from '../components/VideoCarousel';
import { formatDate } from '../utils/format';
import SalesSection from './SalesSection';
import './VideoPage.css';

const Header = styled.header`
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 2rem;

  @media ${device.desktop} {
    padding-bottom: 8rem;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LogoHolder = styled.h1`
  margin: 0;
  width: 6.56rem;
  margin: 1.5vw 0 1vw 0;

  @media ${device.desktop} {
    width: 6rem;
  }
`;

const LogoTaContainer = styled.div`
  width: 100%;
  margin: 2rem 20vw;
`;
const LogoTa = styled.img`
  max-width: 30rem;
  width: 100%;
  @media ${device.desktop} {
    margin-left: -3rem;
  }
`;

const Sign = styled.div`
  width: 6rem;
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.textLight};

  @media ${device.desktop} {
    width: 10rem;
    margin-top: 1.5rem;
  }
`;

const By = styled.div`
  font-family: 'Montserrat';
  font-size: 0.5rem;
  font-weight: 300;

  @media ${device.desktop} {
    font-size: 0.8rem;
  }
`;

const Alex = styled.div`
  font-family: 'Montserrat';
  font-weight: 900;
  font-size: 0.65rem;
  text-transform: uppercase;

  @media ${device.desktop} {
    font-size: 1rem;
  }
`;

const VideoContainer = styled.div`
  margin-top: -1.2rem;

  @media ${device.desktop} {
    margin-top: -7rem;
  }
`;

const Main = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
`;

const SeriesContainer = styled.div`
  color: ${({ theme }) => theme.colors.textDark};
  margin: 1.7rem 1.6rem;

  @media ${device.desktop} {
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme.colors.textDark};

    margin: 6rem 10vw 0 10vw;
  }
`;

const EpisodeContainer = styled.div`
  margin-bottom: 3.4rem;
  @media ${device.desktop} {
    width: 25vw;
  }
`;

const EpisodeWrapper = styled.div`
  @media ${device.maxDesktop} {
    display: flex;
  }
`;

const EpisodeImageContainer = styled.div`
  cursor: pointer;
  position: relative;
  height: fit-content;

  @media ${device.maxDesktop} {
    width: 55%;
    flex: 1;
  }
`;

const EpisodeImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  color: ${({ theme }) => theme.colors.textLight};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${(p) => !p.isAvailable && 'background: rgba(0, 0, 0, 0.3);'}

  .copy {
    font-weight: 900;
    color: white;

    margin-top: 0.8rem;
    font-size: 0.8rem;

    @media ${device.desktop} {
      margin-top: 1.5rem;
      font-size: 1rem;
    }

    @media ${device.bigDesktop} {
      margin-top: 1.5rem;
      font-size: 1.5rem;
    }
  }
`;

const EpisodeDataContainer = styled.div`
  ${(p) => !p.isAvailable && 'opacity: 0.5;'}

  @media ${device.maxDesktop} {
    flex: 1;
    padding-left: 0.75rem;
  }
`;

const EpisodeTitle = styled.h2`
  font-weight: bold;

  font-size: 1.17rem;

  @media ${device.desktop} {
    margin-top: 2.7vw;
    font-weight: bold;
    font-size: 1.6rem;
  }

  @media ${device.bigDesktop} {
    margin-top: 2.7vw;
    font-weight: bold;
    font-size: 2rem;
  }
`;

const EpisodeMeta = styled.div`
  font-weight: 300;

  font-size: 1rem;
  margin-top: 0.2rem;

  @media ${device.desktop} {
    font-size: 1rem;
    margin-top: 0.5rem;
  }

  @media ${device.bigDesktop} {
    font-size: 1.2rem;
    margin-top: 0.5rem;
  }
`;

const EpisodeCopy = styled.div`
  font-size: 1rem;
  margin-top: 1rem;
  font-weight: 500;
  line-height: 1.4;

  ${(p) => !p.isAvailable && 'opacity: 0.5;'}

  @media ${device.desktop} {
    font-size: 1.1rem;
    margin-top: 2rem;
  }

  @media ${device.bigDesktop} {
    font-size: 1.3rem;
    margin-top: 2rem;
  }
`;

const Footer = styled.footer`
  font-weight: 300;
  text-align: center;

  color: ${({ theme }) => theme.colors.textDark};
  background; #fff;

  font-size: 0.63rem;
  padding: 2.5rem 1.6rem;

  @media ${device.desktop} {
    font-size: 1rem;
    padding: 6rem 0;
  }

  @media ${device.bigDesktop} {
    font-size: 1.5rem;
  }
`;

const VideoTease = ({ video, isActive, onClick }) => {
  const theme = useContext(ThemeContext);

  if (typeof window === 'undefined') {
    return <></>;
  }

  const isAvailable = isDateInPast(video.releaseDate);
  const overlayColor = isAvailable
    ? theme.colors.textLight
    : theme.colors.textLight;

  const overlayCopy = () => {
    if (!isAvailable) {
      return `Available ${formatDate(video.releaseDate)}`;
    }

    if (isActive) {
      return 'Playing now';
    }

    return 'Available now';
  };

  return (
    <EpisodeContainer isAvailable={isAvailable}>
      <EpisodeWrapper>
        <EpisodeImageContainer onClick={onClick}>
          <Img fluid={video.cover.childImageSharp.fluid} />
          <EpisodeImageOverlay isAvailable={isAvailable}>
            <VideoButton color={overlayColor} text={`Video ${video.id}`} />
            <div className="copy" style={{ color: overlayColor }}>
              {overlayCopy()}
            </div>
          </EpisodeImageOverlay>
        </EpisodeImageContainer>
        <EpisodeDataContainer isAvailable={isAvailable}>
          <EpisodeTitle>{video.title}</EpisodeTitle>
          <EpisodeMeta>
            with {video.with} | {video.duration}
          </EpisodeMeta>
        </EpisodeDataContainer>
      </EpisodeWrapper>
      <EpisodeCopy isAvailable={isAvailable}>{video.copy}</EpisodeCopy>
    </EpisodeContainer>
  );
};

const VideoPage = ({ index }) => {
  const theme = useContext(ThemeContext);
  const images = useStaticQuery(query);
  const [active, setActive] = useState(index);

  const episodes = [
    {
      id: 1,
      vimeoId: 592058057,
      title: 'The role of spiritual awakening in trauma healing',
      shortTitle: 'The role of spiritual awakening in trauma healing',
      with: 'Dr Gabor Maté',
      duration: '39 mins',
      copy:
        'Dr Gabor Maté explores how healing from our trauma can become a spiritual experience, and the potential for spiritual awakening along the path.',
      cover: images.video1,
      releaseDate: new Date(2021, 7, 29),
    },
    {
      id: 2,
      vimeoId: 592091635,
      title: 'How trauma can be a portal to spiritual awakening',
      shortTitle: 'How trauma can be a portal to spiritual awakening',
      with: 'Hameed Ali (A H Almaas)',
      duration: '43 mins',
      copy:
        'Hameed Ali (A H Almaas) explains the process of spiritual awakening, and how our suffering can become a gateway to supporting the process.',
      cover: images.video2,
      releaseDate: new Date(2021, 8, 6),
    },
    {
      id: 3,
      vimeoId: 592099232,
      title: 'Next steps for Trauma & Awakening',
      shortTitle: 'Next steps for Trauma & Awakening',
      with: 'Alex Howard',
      duration: '17 mins',
      copy:
        'Alex Howard introduces the upcoming Trauma & Awakening program with Dr Gabor Maté and Hameed Ali (A H Almaas) and what to expect as part of it.',
      cover: images.video3,
      releaseDate: new Date(2021, 8, 8),
    },
  ];

  // const showSales = new Date() > episodes[episodes.length - 1].releaseDate;
  const showSales = true;

  return (
    <>
      <GlobalStyle2 />
      <SEO title="The Trauma &amp; Awakening Program" />
      <Main>
        <Header>
          <TitleRow>
            {/* <Sign /> */}
            {/* <LogoHolder>
              <Logo width="100%" color={theme.colors.white} />
            </LogoHolder> */}
            {/* <Sign>
              <By>A program by</By>
              <Alex>Alex Howard</Alex>
            </Sign> */}
            <LogoTaContainer>
              <LogoTa src={LogoTaFlat} />
            </LogoTaContainer>
          </TitleRow>
          <VideoPager videos={episodes} active={active} setActive={setActive} />
        </Header>

        <VideoContainer>
          <VideoCarousel
            videos={episodes}
            active={active}
            setActive={setActive}
          />
        </VideoContainer>

        {!showSales || active <= 2 ? (
          <SeriesContainer>
            {episodes.map((e) => (
              <VideoTease
                key={e.id}
                video={e}
                isActive={e.id === active}
                onClick={() => setActive(e.id)}
              />
            ))}
          </SeriesContainer>
        ) : (
          <SalesSection />
        )}

      </Main>

      <Footer>
        <div>
          This website and its content are copyright &copy; Alex Howard{' '}
          {new Date().getFullYear()}.
        </div>
        <div> All Rights Reserved.</div>
      </Footer>
    </>
  );
};

const query = graphql`
  query {
    video1: file(relativePath: { eq: "video1.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    video2: file(relativePath: { eq: "video2.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    video3: file(relativePath: { eq: "video3.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`;

export default VideoPage;
